import { ReactNode, useState } from 'react';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { showWarning } from 'src/utils/toast/toastUtil';
import { validateString } from 'src/utils/string/stringUtil';
import { postTQC5020_R01NewInspectItem } from './Api_TQC5020_R01';
import { defaultTQC5020_R01DetailDto, TQC5020_R01DetailDto } from './Dto_TQC5020_R01';

interface Props {
	itemCd: string;
	itemGroup: string;
	onClose: () => void;
	onSaved: () => void;
}

interface ModalFieldProps {
	label: string;
	children: ReactNode;
}

/** 모달 폼 라벨 + 입력 영역 (SonarQube S6772 JSX 공백 모호성 방지) */
const ModalField = ({ label, children }: ModalFieldProps) => (
	<div className="flex flex-col text-sm gap-1">
		<span>{label}</span>
		{children}
	</div>
);

export const NewItemModal_TQC5020_R01 = ({ itemCd, itemGroup, onClose, onSaved }: Props) => {
	const {
		inspectionTypeList, inspectionRangeList, inspectionValueList, inspectMethodList, unitList,
	} = useCommonCodeStore();

	const [form, setForm] = useState<TQC5020_R01DetailDto>({
		...defaultTQC5020_R01DetailDto,
		itemCd,
		inspItemGroup: itemGroup,
	});

	const setField = <K extends keyof TQC5020_R01DetailDto>(key: K, value: TQC5020_R01DetailDto[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const handleSave = async () => {
		if (!validateString(form.inspType)) { showWarning('검사구분을 선택해주세요.'); return; }
		if (!validateString(form.inspItemCd)) { showWarning('검사코드를 입력해주세요.'); return; }
		if (!validateString(form.inspItemNm)) { showWarning('검사내용을 입력해주세요.'); return; }
		if (!validateString(form.valueType)) { showWarning('검사값 유형을 선택해주세요.'); return; }
		if (!validateString(form.inspMethod)) { showWarning('검사방법을 선택해주세요.'); return; }

		const response = await postTQC5020_R01NewInspectItem(form);
		if (response.data.successFlag) {
			onSaved();
			onClose();
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
			<div className="bg-white rounded-md shadow-lg w-[560px] p-5">
				<h3 className="text-base font-semibold mb-4">검사항목 등록</h3>

				<div className="grid grid-cols-2 gap-3">
					<ModalField label="품목그룹">
						<input className="border rounded px-2 py-1 bg-gray-100" value={form.inspItemGroup} disabled />
					</ModalField>
					<ModalField label="검사구분">
						<select
							className="border rounded px-2 py-1"
							value={form.inspType}
							onChange={(e) => setField('inspType', e.target.value)}
						>
							<option value="">선택</option>
							{inspectionTypeList.map((code) => (
								<option key={code.minorCd} value={code.minorCd}>{code.codeNm}</option>
							))}
						</select>
					</ModalField>

					<ModalField label="검사코드">
						<input
							className="border rounded px-2 py-1"
							value={form.inspItemCd}
							onChange={(e) => setField('inspItemCd', e.target.value)}
						/>
					</ModalField>
					<ModalField label="검사내용">
						<input
							className="border rounded px-2 py-1"
							value={form.inspItemNm}
							onChange={(e) => setField('inspItemNm', e.target.value)}
						/>
					</ModalField>

					<ModalField label="값1">
						<input
							className="border rounded px-2 py-1"
							value={form.specValue1}
							onChange={(e) => setField('specValue1', e.target.value)}
						/>
					</ModalField>
					<ModalField label="범위">
						<select
							className="border rounded px-2 py-1"
							value={form.specRange}
							onChange={(e) => setField('specRange', e.target.value)}
						>
							<option value="">선택</option>
							{inspectionRangeList.map((code) => (
								<option key={code.minorCd} value={code.minorCd}>{code.codeNm}</option>
							))}
						</select>
					</ModalField>

					<ModalField label="값2">
						<input
							className="border rounded px-2 py-1"
							value={form.specValue2}
							onChange={(e) => setField('specValue2', e.target.value)}
						/>
					</ModalField>
					<ModalField label="단위">
						<select
							className="border rounded px-2 py-1"
							value={form.inspUnit}
							onChange={(e) => setField('inspUnit', e.target.value)}
						>
							<option value="">선택</option>
							{unitList.map((code) => (
								<option key={code.minorCd} value={code.minorCd}>{code.codeNm}</option>
							))}
						</select>
					</ModalField>

					<ModalField label="검사값 유형">
						<select
							className="border rounded px-2 py-1"
							value={form.valueType}
							onChange={(e) => setField('valueType', e.target.value)}
						>
							<option value="">선택</option>
							{inspectionValueList.map((code) => (
								<option key={code.minorCd} value={code.minorCd}>{code.codeNm}</option>
							))}
						</select>
					</ModalField>
					<ModalField label="검사방법">
						<select
							className="border rounded px-2 py-1"
							value={form.inspMethod}
							onChange={(e) => setField('inspMethod', e.target.value)}
						>
							<option value="">선택</option>
							{inspectMethodList.map((code) => (
								<option key={code.minorCd} value={code.minorCd}>{code.codeNm}</option>
							))}
						</select>
					</ModalField>
				</div>

				<div className="flex justify-center gap-3 mt-5">
					<button className="px-4 py-1.5 bg-blue-600 text-white rounded" onClick={handleSave}>저장</button>
					<button className="px-4 py-1.5 bg-gray-200 rounded" onClick={onClose}>취소</button>
				</div>
			</div>
		</div>
	);
};
